'use client';

import * as React from 'react';
import {Mail, Instagram, Facebook} from 'lucide-react';
import {useParams} from 'next/navigation';
import {useResponsive} from '@purpose/ui/hooks/useResponsive';
import useTheme from '@purpose/ui/hooks/useTheme';
import {cn} from '@purpose/ui/lib/utils';

export interface FooterLink {
  label: string;
  route: string;
  prefix: string;
}

export interface FooterProps {
  companyName?: string;
  orgNumber?: string;
  address?: string;
  phone?: string;
  hours?: string;
  email?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  color?: string | null;
  ref?: React.RefObject<HTMLElement> | React.RefObject<null>;
  footerLinks: FooterLink[];
  labels: {
    address: string;
    number: string;
  };
  logo?: React.ReactNode;
}

const Footer = ({
  companyName = 'Purpose IT',
  orgNumber = '934 916 042',
  address = 'Munkedamsveien 35, 0250 Oslo',
  phone,
  hours,
  email = 'support@purposeit.no',
  color,
  ref,
  footerLinks,
  labels,
  logo
}: FooterProps) => {
  const {isTabletOrSmaller, isLaptopOrSmaller} = useResponsive();
  const {colors} = useTheme();
  const {locale} = useParams<{locale: string}>();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const backgroundColor = color || colors.primary[110];

  return (
    <footer
      ref={ref}
      className={cn(
        'flex flex-shrink-0 items-start justify-center py-10',
        isTabletOrSmaller && 'items-center'
      )}
      style={{
        backgroundColor: mounted ? backgroundColor : undefined
      }}
    >
      <div
        className={cn(
          'flex w-full justify-between',
          isTabletOrSmaller ? 'flex-col items-center' : 'flex-row items-start'
        )}
        style={{paddingLeft: '32px', paddingRight: '32px'}}
      >
        {/* Company Info */}
        <div
          className={cn(
            'flex flex-col gap-5',
            isTabletOrSmaller
              ? 'items-center pb-6 text-center'
              : 'items-start pb-0 text-left'
          )}
        >
          {logo && (
            <div className={isTabletOrSmaller ? 'mx-auto' : ''}>{logo}</div>
          )}
          <div className="text-secondary-60 text-sm">
            {companyName}
            <br />
            {labels.number}: {orgNumber}
            <br />
            {labels.address}: {address}
            <br />
            {phone}
            <br />
            {hours}
          </div>
        </div>

        {/* Links and Social */}
        <div
          className={cn(
            'flex flex-col justify-between gap-6',
            isTabletOrSmaller ? 'items-center' : 'items-end'
          )}
        >
          {/* Footer Links */}
          <div className="text-secondary-60 flex flex-row gap-8">
            {footerLinks?.map(link => (
              <a
                key={link.route}
                href={`${link.prefix}/${locale !== 'no' ? `/${locale}` : ''}${link.route}`}
                className="text-secondary-60 hover:text-secondary-40 text-sm no-underline transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Social Links */}
          <div className="text-secondary-60 flex gap-8">
            <a
              href={`mailto:${email}`}
              className="text-secondary-60 hover:text-secondary-40 flex items-center gap-2 no-underline transition-colors"
            >
              <Mail className="h-5 w-5" />
              {!isLaptopOrSmaller && <span className="text-sm">{email}</span>}
            </a>
            {/* Instagram and Facebook links can be uncommented when needed */}
            {/* {instagramUrl && (
              <a
                href={`https://${instagramUrl}`}
                className="flex items-center gap-2 text-secondary-60 no-underline hover:text-secondary-40 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram className="w-5 h-5" />
                {!isLaptopOrSmaller && (
                  <span className="text-sm">{instagramUrl}</span>
                )}
              </a>
            )}
            {facebookUrl && (
              <a
                href={`https://${facebookUrl}`}
                className="flex items-center gap-2 text-secondary-60 no-underline hover:text-secondary-40 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook className="w-5 h-5" />
                {!isLaptopOrSmaller && (
                  <span className="text-sm">{facebookUrl}</span>
                )}
              </a>
            )} */}
          </div>
        </div>
      </div>
    </footer>
  );
};

export {Footer};
